import { isEquallySpacedLayoutCode } from "entities/configurator/model/layoutMeta";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";
import { applyHeightToEigenwijs } from "./eigenwijsHeight";
import {
  BeamData,
  EigenwijsOptions,
  LayoutOptions,
  SizeObject,
} from "./DoorOptionsProvider.types";

const selfAssemblyOpdekDoorHeight = 2310;
const trapDefaultDoorHeight = 2960;
const kaderDefaultDoorHeight = 2115;
const maxHeightDoorDefault = 3000;
const kaderDefaultDoorWidth = 880;

const maxHeightDoorCodes = new Set([
  "taatsdeur",
  "schuifdeur",
  "dubbel",
  "dubbeleschuifdeur",
  "taatsdeur1paneel",
  "schuifdeur1paneel",
  "taatsdeur2paneel",
  "schuifdeur2paneel",
  "dubbel1paneel",
  "dubbeleschuifdeur1paneel",
  "dubbel2paneel",
  "dubbeleschuifdeur2paneel",
  "taatsdeurpaneelpaneel",
  "taatsdeurtaatsdeurpaneelpaneel",
  "schuifdeurpaneelpaneel",
  "schuifdeurpaneelschuifdeur",
  "schuifdeurschuifdeurpaneelpaneel",
  "schuifdeurpaneelpaneelschuifdeur",
  "schuifpaneelschuiftrap",
  "scharnierpaneelscharniertrap",
]);

const kaderDoorCodes = new Set([
  "kaderdeur",
  "dubbelekaderdeur",
  "zakelijkkozijn",
]);

const parseQueryNumberArray = (encodedValue?: string): number[] => {
  if (!encodedValue) {
    return [];
  }

  try {
    return JSON.parse(decodeURIComponent(encodedValue));
  } catch {
    return [];
  }
};

const cloneEigenwijsOptions = (
  options: EigenwijsOptions,
): EigenwijsOptions => ({
  selectedBeam: options.selectedBeam ?? null,
  beams: options.beams.map((beam) => ({ ...beam })),
  sizeObjects: options.sizeObjects.map((sizeObject) => ({ ...sizeObject })),
});

const getInitialLayoutHeight = ({
  doorCode,
  searchedHeight,
  selfAssembly,
}: {
  doorCode: string;
  searchedHeight?: string;
  selfAssembly: boolean;
}): number => {
  if (searchedHeight) {
    return parseInt(searchedHeight, 10);
  }

  if (doorCode === "opdekdeur" && selfAssembly) {
    return selfAssemblyOpdekDoorHeight;
  }

  if (doorCode.includes("trap")) {
    return trapDefaultDoorHeight;
  }

  if (doorCode === "opdekdeur" || doorCode.includes("stompedeur")) {
    return selfAssemblyOpdekDoorHeight;
  }

  if (kaderDoorCodes.has(doorCode)) {
    return kaderDefaultDoorHeight;
  }

  if (maxHeightDoorCodes.has(doorCode)) {
    return maxHeightDoorDefault;
  }

  return defaultDoorHeight;
};

export const buildInitialSizeObjects = ({
  doorCode,
  elementCount,
  height,
  searchedWidth,
}: {
  doorCode: string;
  elementCount: number;
  height: number;
  searchedWidth?: string;
}): SizeObject[] => {
  const searchedWidths = parseQueryNumberArray(searchedWidth);
  const hasSearchedWidth = Boolean(searchedWidth);
  const defaultWidth =
    !hasSearchedWidth && kaderDoorCodes.has(doorCode)
      ? kaderDefaultDoorWidth
      : defaultDoorWidth;

  return Array.from({ length: elementCount }, (_, index) => ({
    customHeight: height,
    customWidth: searchedWidths[index] || defaultWidth,
  }));
};

export const updateAnchorBeams = (
  doorHeight: number,
  beams: BeamData[],
): BeamData[] => {
  const target = doorHeight / 2;
  const anchorCount = 2;
  const beamHeights = beams
    .map((beam) => beam.height)
    .sort((left, right) => {
      const compare = Math.abs(left - target) - Math.abs(right - target);

      if (compare === 0) {
        return -Math.min(left, right);
      }

      return compare;
    });
  const anchorHeights = beamHeights
    .slice(0, anchorCount)
    .sort((left, right) => left - right);

  return beams.map((beam) => ({
    ...beam,
    handleAnchor: anchorHeights.includes(beam.height),
  }));
};

export const getDefaultLayoutBeamData = (
  doorHeight: number,
  beams: BeamData[],
): BeamData[] => {
  return beams.map((beam, index) => {
    const height = Math.round(doorHeight / (beams.length + 1));

    return {
      ...beam,
      height: height * (index + 1),
      handleAnchor: index <= 1,
    };
  });
};

export const buildInitialLayoutEigenwijs = ({
  createId,
  doorCode,
  elementCount,
  layoutCode,
  layoutOptions,
  searchedBeam,
  searchedHeight,
  searchedWidth,
  selfAssembly,
}: {
  createId?: () => string;
  doorCode: string;
  elementCount: number;
  layoutCode: string;
  layoutOptions: readonly LayoutOptions[];
  searchedBeam?: string;
  searchedHeight?: string;
  searchedWidth?: string;
  selfAssembly: boolean;
}): EigenwijsOptions | null => {
  const matchedLayout = layoutOptions.find(
    (layoutOption) => layoutOption.name === layoutCode,
  );

  if (!matchedLayout) {
    return null;
  }

  const initialOptions = cloneEigenwijsOptions(matchedLayout.options);
  const initialHeight = getInitialLayoutHeight({
    doorCode,
    searchedHeight,
    selfAssembly,
  });

  initialOptions.sizeObjects = buildInitialSizeObjects({
    doorCode,
    elementCount,
    height: initialHeight,
    searchedWidth,
  });

  if (layoutCode === "eigenwijs" && searchedBeam) {
    const searchedBeamHeights = parseQueryNumberArray(searchedBeam);

    if (searchedBeamHeights.length > 0) {
      const createBeamId = createId ?? (() => "");

      initialOptions.beams = updateAnchorBeams(
        initialHeight,
        searchedBeamHeights.map((height) => ({
          id: createBeamId(),
          height,
          adjustable: true,
          handleAnchor: false,
        })),
      );
    }
  }

  if (isEquallySpacedLayoutCode(layoutCode)) {
    initialOptions.beams = getDefaultLayoutBeamData(
      initialHeight,
      initialOptions.beams,
    );
  }

  const seededHeight =
    initialOptions.sizeObjects[0]?.customHeight ?? initialHeight;

  return applyHeightToEigenwijs(initialOptions, seededHeight, layoutCode);
};
